import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Loading from '../common/Loading';
import Alert from '../common/Alert';
import { formatCurrency, formatDate } from '../../utils/formatters';
import EmpStatusPill from './EmpStatusPill';
import './OrderList.css';

const OrderList = ({
  orders = [],
  loading = false,
  error = null,
  onOrderClick,
  isEmployee = false,
  selectedIds = null,
  onToggleSelect = null,
  onToggleSelectAll = null,
}) => {
  const navigate = useNavigate();
  const selectable = isEmployee && selectedIds !== null;

  const handleRowClick = (orderId, e) => {
    if (selectable && (e.target.type === 'checkbox' || e.target.closest('[data-checkbox]'))) return;
    if (onOrderClick) {
      onOrderClick(orderId);
    } else {
      const basePath = isEmployee ? '/employee/orders' : '/orders';
      navigate(`${basePath}/${orderId}`);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="order-list__state">
        <Loading />
        <p className="order-list__state-text">Loading orders…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-list__state">
        <Alert type="error">
          {error.message || 'Failed to load orders. Please try again.'}
        </Alert>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="order-list__empty">
        <p className="order-list__empty-label">No Orders Yet</p>
        <p className="order-list__empty-sub">
          {isEmployee
            ? 'No orders have been placed.'
            : 'Place your first order to get started.'}
        </p>
      </div>
    );
  }

  const gridClass = isEmployee ? 'order-list--employee' : '';
  const allSelected = selectable && orders.length > 0 && orders.every((o) => selectedIds.has(o.id));
  const someSelected = selectable && orders.some((o) => selectedIds.has(o.id));

  return (
    <div className={`order-list ${gridClass}${selectable ? ' order-list--selectable' : ''}`}>
      {/* Column headers */}
      <div className="order-list__thead">
        {selectable && (
          <span className="order-list__col-check" data-checkbox>
            <input
              type="checkbox"
              className="order-list__checkbox"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
              onChange={() => onToggleSelectAll(orders.map((o) => o.id))}
              aria-label="Select all orders"
            />
          </span>
        )}
        <span className="order-list__col-label">Order</span>
        <span className="order-list__col-label order-list__col-label--hide-sm">Delivery</span>
        {isEmployee && (
          <span className="order-list__col-label order-list__col-label--hide-sm">Region</span>
        )}
        {isEmployee && (
          <span className="order-list__col-label order-list__col-label--hide-sm">Company</span>
        )}
        <span className="order-list__col-label order-list__col-label--right order-list__col-label--hide-sm">Total</span>
        <span className="order-list__col-label order-list__col-label--hide-sm">Status</span>
        <span />
      </div>

      {orders.map((order, index) => {
        const total = order.total_amount_dollars ?? order.total_amount;
        const isSelected = selectable && selectedIds.has(order.id);

        return (
          <div
            key={order.id}
            className={`order-list__row${isSelected ? ' order-list__row--selected' : ''}`}
            style={{ animationDelay: `${index * 0.045}s` }}
            onClick={(e) => handleRowClick(order.id, e)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleRowClick(order.id, e)}
          >
            {selectable && (
              <div className="order-list__check-cell" data-checkbox onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="order-list__checkbox"
                  checked={isSelected}
                  onChange={() => onToggleSelect(order.id)}
                  aria-label={`Select order ${order.order_number}`}
                />
              </div>
            )}
            {/* Order number + placed date + mobile inline meta */}
            <div className="order-list__row-primary">
              <span className="order-list__order-number">{order.order_number}</span>
              <span className="order-list__placed-date">
                Placed {order.created_at ? formatDate(order.created_at) : '—'}
              </span>
              {isEmployee && order.po_number && (
                <span className="order-list__po">PO: {order.po_number}</span>
              )}
              {/* Mobile-only: total + status inline under the date */}
              <div className="order-list__row-meta">
                <span className="order-list__total order-list__total--meta">
                  {total != null ? formatCurrency(total) : '—'}
                </span>
                <EmpStatusPill status={order.status} className="emp-status-pill--sm" />
              </div>
            </div>

            {/* Delivery date */}
            <div className="order-list__delivery order-list__col--hide-sm">
              {order.delivery_date ? formatDate(order.delivery_date) : '—'}
            </div>

            {/* Region (employee) */}
            {isEmployee && (
              <div className="order-list__region order-list__col--hide-sm">
                {order.delivery_region || '—'}
              </div>
            )}

            {/* Company (employee) */}
            {isEmployee && (
              <div className="order-list__company order-list__col--hide-sm">
                {order.company_name || '—'}
              </div>
            )}

            {/* Total — desktop only */}
            <div className="order-list__total">
              {total != null ? formatCurrency(total) : '—'}
            </div>

            {/* Status pill — desktop only */}
            <div className="order-list__status">
              <EmpStatusPill status={order.status} />
            </div>

            {/* Arrow */}
            <div className="order-list__arrow">→</div>
          </div>
        );
      })}
    </div>
  );
};

OrderList.propTypes = {
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      order_number: PropTypes.string,
      status: PropTypes.string,
      delivery_date: PropTypes.string,
      total_amount_dollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      total_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      created_at: PropTypes.string,
      company_name: PropTypes.string,
      delivery_region: PropTypes.string,
      po_number: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.shape({ message: PropTypes.string }),
  onOrderClick: PropTypes.func,
  isEmployee: PropTypes.bool,
  selectedIds: PropTypes.instanceOf(Set),
  onToggleSelect: PropTypes.func,
  onToggleSelectAll: PropTypes.func,
};

export default OrderList;
